import { Volume2 } from 'lucide-react'
import type { Exercise } from '@/types'
import AudioPlayer from '@/components/common/AudioPlayer'
import MultipleChoiceExercise from './MultipleChoiceExercise'
import EditableText from '@/components/edit/EditableText'
import { useEditMode } from '@/contexts/EditModeContext'
import { useAuth } from '@/contexts/AuthContext'
import { saveField } from '@/lib/editApi'

interface Props {
  exercise: Exercise
  selectedAnswer: string
  onSelectAnswer: (answer: string) => void
  isSubmitted: boolean
  isCorrect: boolean
  onExerciseUpdated?: (updated: Exercise) => void
}

export default function ListeningExercise({ exercise, selectedAnswer, onSelectAnswer, isSubmitted, isCorrect, onExerciseUpdated }: Props) {
  const { isEditMode } = useEditMode()
  const { profile } = useAuth()

  const saveQuestion = async (newText: string) => {
    if (!profile?.id) return { error: new Error('No autenticado') }
    const { error } = await saveField({
      table: 'exercises', id: exercise.id, field: 'question',
      oldValue: exercise.question || '', newValue: newText, editedBy: profile.id
    })
    if (!error) onExerciseUpdated?.({ ...exercise, question: newText })
    return { error }
  }

  return (
    <div className="space-y-4">
      {/* Reproductor de audio prominente */}
      {exercise.audio_url && (
        <div className="p-5 bg-blue-50 border border-blue-200 rounded-xl">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center">
              <Volume2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-medium text-blue-900">Escucha el audio</p>
              <p className="text-xs text-blue-600">Puedes reproducirlo varias veces</p>
            </div>
          </div>
          <AudioPlayer src={exercise.audio_url} className="w-full" />
        </div>
      )}

      {/* Pregunta */}
      {(exercise.question || isEditMode) && (
        <EditableText
          as="p"
          value={exercise.question}
          customSave={saveQuestion}
          placeholder="(sin pregunta)"
          multiline
          className="text-lg font-medium text-gray-900 block"
        />
      )}

      {/* Opciones multiple choice */}
      <MultipleChoiceExercise
        exercise={exercise}
        selectedAnswer={selectedAnswer}
        onSelectAnswer={onSelectAnswer}
        isSubmitted={isSubmitted}
        isCorrect={isCorrect}
        onExerciseUpdated={onExerciseUpdated}
      />
    </div>
  )
}
